import PropTypes from 'prop-types';
import { useState } from 'react';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Helmet from '../../components/Helmet';
import { Box, Typography } from '@mui/material';
import {
  AUTH_KEY,
  CircularProgress,
  clearAuthCookie,
  getCookieValue,
  windowExists,
} from '@classity-edtech/ui-base';
import { Header } from '@classity-edtech/ui-base/dist/components/Displays';
import { useEffect } from 'react';
import { webPaths } from '../../routes/paths';
import { useGetMyProfileQuery, useLazyGetMyProfileQuery } from '../../services/users';
import { useGetPageContentQuery } from '../../services/seoContent';
import { useLogoutMutation } from '../../services/authentication';
import { useLazyGetMyResponsesQuery } from '../../services/response';
import { Roles, FreeFormName, FormStatus } from '@classity-edtech/platform-utils';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import Auth from '../../components/Auth';

export interface HomeProps extends MuiContainerProps {}

export const Home = ({ className }: HomeProps) => {
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const [getUser] = useLazyGetMyProfileQuery();
  const [getMyResponses] = useLazyGetMyResponsesQuery();

  const webUi = (windowExists.__UI_CONFIG__ as any)?.webUi?.url;
  const [page] = useState(1);
  const limit = 1;

  const { data: pageContent } = useGetPageContentQuery({
    tag: 'learn_home',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!pageContent) {
      return {
        seo: {
          title: pageContent?.seo?.fields?.titleTag,
          keywords: pageContent?.seo?.fields?.keywords,
          description: pageContent?.seo?.fields?.metaDescription,
          metaRobotsIndex: pageContent?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const handleAuthAction = async (role: string) => {
    if (role === Roles.ADMIN) return `/${webPaths.applications}`;
    if (role === Roles.PROSPECT_TUTOR) {
      const response = await getMyResponses({
        formName: FreeFormName.ONBOARDING_TUTOR,
        page,
        limit,
      });

      if ('data' in response && response.data?.[0]?.status === FormStatus.SUBMITTED) {
        return `/${webPaths.tutorDashboard}`;
      }

      return `/${webPaths.tutorOnboarding}`;
    }
    if (role === Roles.STUDENT) {
      const freshUser = await getUser();
      const profile: Record<string, any> | undefined = freshUser?.data?.profile;

      if (!profile?.ids) {
        return `/${webPaths.studentProfile}`;
      }

      return `/${webPaths.learnerDashboard}`;
    }
    if (role === Roles.TUTOR) return `/${webPaths.tutorDashboard}`;
  };

  useEffect(() => {
    if (!loadingUser && !uninitialisedUser) {
      if (user == null) {
        localStorage.removeItem('token');
        navigate(`${webPaths.root}`, { replace: true });
      } else {
        handleAuthAction(user.roleId).then(url => {
          if (url) (windowExists as any).location = url;
        });
      }
    }
  }, [user, loadingUser, uninitialisedUser]);

  const isLoading = loadingUser || uninitialisedUser;

  const memberProfile = {
    memberId: user?.profile?.ids?.find((p: any) => p?.name === 'uid')?.value || '',
    logoutAction: () => {
      const clientId = getCookieValue(AUTH_KEY);

      logout({ clientId });

      clearAuthCookie();
      navigate(`${webPaths.root}`);
    },
  };

  return (
    <Box
      className={className}
      sx={{
        height: { xs: 'auto', md: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'auto', md: 'hidden' },
      }}
    >
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />

      <Header sx={{ px: { xs: 2.5, md: 4 }, flexShrink: 0 }} profileDropdown={memberProfile} />

      <Box
        sx={{
          flex: { md: 1 },
          overflow: { xs: 'visible', md: 'hidden' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Auth
            onInstructorSignIn={() => navigate(`/${webPaths.teachSignin}`)}
            onInstructorCreateAccount={() => navigate(`/${webPaths.teachSignup}`)}
            onLearnerSignIn={() => navigate(`/${webPaths.signin}`)}
            onLearnerCreateAccount={() => navigate(`/${webPaths.signup}`)}
          />
        )}
      </Box>

      <Box
        sx={{
          py: 1,
          px: { xs: 2.5, md: 4 },
          flexShrink: 0,
          background: globalColours['$global-colour-secondary-brand-4'],
        }}
      >
        <Typography variant='body3' sx={{ color: globalColours['$global-colour-base-contrast'] }}>
          © 2026 Classity EdTech Ltd. All rights reserved.{' '}
        </Typography>
      </Box>
    </Box>
  );
};

Home.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Home.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Home)(
  ({ theme }) => `
`,
);
